import { Stripe, StripeElements } from "@stripe/stripe-js";
import React, { forwardRef } from "react";
import styled from "styled-components";

import { PaymentRequiredError } from "apis/errors";
import { Button } from "js/Components/Button";
import { FlexLayout } from "js/Components/FlexLayout";
import { StripeRadarError } from "js/controllers/BillingController";
import CheckoutController, { CheckoutControllerState } from "js/controllers/CheckoutController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger(LogGroup.CHECKOUT);

const SubmitButton = styled(Button) <{ fullWidth?: boolean }>`
    width: ${props => props.fullWidth ? "100%" : "200px"};
`;

const CancelButton = styled(Button)`
    background: transparent;
    color: ${themeColors.ui_blue};
`;

interface SubmissionButtonsProps {
    stripe: Stripe;
    elements: StripeElements;
    submitLabel?: string;
    cancelLabel?: string;
    loadingLabel?: string;
}

export const SubmissionButtons = CheckoutController.withState(forwardRef<HTMLButtonElement, SubmissionButtonsProps & CheckoutControllerState>(
    function SubmissionButtons({ fetching, stripe, elements }, ref) {
        const { submitLabel, progressTitle, progressMessage, cancelLabel } = CheckoutController.labels;

        const canCheckout = CheckoutController.canCheckout;
        const canCancel = CheckoutController.canCancel;

        const handleSubmit = async () => {
            withProgressDialog({
                title: progressTitle,
                message: progressMessage,
                action: async () => {
                    try {
                        await CheckoutController.checkout({ stripe, elements });
                    } catch (err) {
                        logger.error(err, "CheckoutController.checkout() failed");

                        if (err instanceof PaymentRequiredError) {
                            ShowErrorDialog({
                                title: t("There was a problem processing your payment"),
                                message: err.message
                            });
                        } else if (err instanceof StripeRadarError) {
                            ShowErrorDialog({
                                title: t("There was a problem processing your payment"),
                                message: t("We had trouble validating your card; please use a different payment method"),
                            });
                        } else {
                            ShowErrorDialog({
                                title: t("There was a problem upgrading your plan"),
                                message: err.message
                            });
                        }
                    }
                }
            });
        };

        const handleCancel = () => {
            CheckoutController.cancel();
        };

        return (
            <FlexLayout direction="row" align="center" justify="space-between" width="fill">
                {canCancel && (
                    <CancelButton
                        data-test-id="payment-form-cancel-button"
                        onClick={handleCancel}
                        disabled={fetching}
                        large
                    >
                        {cancelLabel}
                    </CancelButton>
                )}
                <SubmitButton
                    ref={ref}
                    data-test-id="payment-form-submit-button"
                    onClick={handleSubmit}
                    disabled={!canCheckout || fetching}
                    fullWidth={!canCancel}
                    blue large
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (canCheckout && !fetching && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            handleSubmit();
                        }
                    }}
                >
                    {submitLabel}
                </SubmitButton>
            </FlexLayout>
        );
    }
));
